package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Caption
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Tr

/**
 * One line of the ledger: what was counted, and how many of it.
 *
 * [value] is pre-formatted text and not a number, because the thousands separator, the currency and
 * the **word that replaces a number in a money slot** (hard rule 11 — "No cap", not `0`) are the
 * caller's decisions and change with locale. That is why [isZero] has to be read back off the
 * string; where that reading is wrong, set it.
 */
data class LStatRowItem(
    val label: String,
    val value: String,
    /** The closing "Left" / "Net" line: heavier, and separated by a rule rather than a hairline. */
    val emphasis: Boolean = false,
    val isZero: Boolean = value.readsAsZero(),
)

/**
 * Zero as a reader sees it — `0`, `0.00`, `$0.00`, `0 credits`: every digit present is a zero.
 *
 * Compared by digit *value*, not against the character `'0'`. A product about languages renders
 * `٠٫٠٠` and `०` too, and `'٠' != '0'` is true — so a character check would file an Arabic-Indic
 * zero as a non-zero figure and hand it `textPrimary`, breaking rule 9 in precisely the locales
 * this component exists to survive.
 *
 * A value carrying no digit at all is not a number, so hard rule 9 has nothing to say about it and
 * it keeps `textPrimary`. That is deliberate: rule 11's word-instead-of-a-number is a real answer,
 * and greying it out would file it under "nothing here".
 *
 * `internal` rather than `private` because [LStatTile] draws its figure by the same rule and had
 * copied the body to reach it. Two identical functions in one design system is one function and a
 * pending disagreement: the next locale that needs a digit set added here would have got it in the
 * ledger and not in the tiles above it.
 */
internal fun String.readsAsZero(): Boolean =
    any { it.isDigit() } && none { it.isDigit() && it.digitToInt() != 0 }

val LStatRowGroupStyle = CssStyle.base {
    Modifier.width(100.percent)
}

/**
 * One 44-tall line. 44 is pinned by the spec, and clears the 40 hit floor even though no row here
 * is a control.
 *
 * The between-row divider is a **top** border on every row but the first, rather than a bottom
 * border on every row but the last. Both draw the same lines in the same places — but only the top
 * border is the one an emphasised row can replace with its own rule. A bottom border on the row
 * above would stack a second line beneath it, and a totals rule drawn 2px thick is a totals rule
 * nobody asked for.
 *
 * The **last** row then carries a bottom border of its own, which no between-row scheme draws.
 * billing.md §6.1 asks for a hairline "under every row including the last; the last one closes the
 * ledger" — without it the stack stops rather than ends, and the final figure, which is the one
 * somebody came to read, is the only one left sitting on nothing.
 *
 * No `justify-content`: the label is `flex-grow: 1`, so there is never free space left to
 * distribute and `space-between` would be a declaration that can never fire.
 */
val LStatRowStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .gap(Space.XL)
            .height(44.px)
    }
    cssRule(":not(:first-child)") {
        Modifier.styleModifier {
            property("border-top", "1px solid ${ColorTokens.Border.value()}")
        }
    }
    cssRule(":last-child") {
        Modifier.styleModifier {
            property("border-bottom", "1px solid ${ColorTokens.Border.value()}")
        }
    }
}

val LStatRowLabelStyle = CssStyle.base {
    Modifier
        .flexGrow(1)
        .color(ColorTokens.TextSecondary.value())
        .fontSize(TypeScale.BodySmall)
        // The row is a fixed 44 under `align-items: center`, so leading is not load-bearing: each
        // half's box is centred and its half-leading is symmetric either way. 1 is simply the value
        // that cannot make a label box taller than the row it has to sit inside.
        .lineHeight(1)
}

val LStatRowValueStyle = CssStyle.base {
    Modifier
        // `value/16.5` — 16.5 / 500 (billing.md §1.3), which is also run-receipt §5.3's money row.
        // Not `HeadingSmall`: 21 is the ramp's *heading* step, and a ledger figure set larger than
        // the heading of the card holding it is a figure shouting over its own label.
        .fontSize(TypeScale.Body)
        .fontWeight(500)
        .lineHeight(1)
        .textAlign(TextAlign.End)
        .tabularNumbers()
        .styleModifier {
            // 72 fixed, so a 1,204 and a 3 end on the same edge and the column does not shift as
            // figures refresh. `flex-shrink: 0` holds that floor; the flex item's own default
            // `min-width: auto` still lets a figure wider than 72 push the box out instead of
            // spilling past it. A number in a money slot is never clipped to fit a box.
            property("flex", "0 0 72px")
            // …and never wrapped. This slot holds `0 credits` and rule 11's word-instead-of-a-
            // number as well as bare digits, and a value containing a space has a min-content width
            // of one word — so it would break onto a second line inside a row fixed at 44 and spill
            // over its neighbour. `nowrap` keeps the box growing sideways, which `min-width: auto`
            // above already allows.
            property("white-space", "nowrap")
        }
}

/**
 * A label and a number, stacked into a small table of totals.
 *
 * Owns its own dividers, which is why it takes the whole list rather than being called once per
 * row: "a hairline under every row, and a heavier rule above the totals line but never above the
 * first" is a fact about the set, and a caller assembling it by hand gets it wrong the first time
 * a row becomes conditional. Billing's credit
 * table has exactly such a row — "Granted" appears only above zero.
 *
 * **The numbers ship twice.** The painted stack is a picture of a table, which hard rule 7 says
 * owes a real one; [caption] is required because a table with no accessible name is a grid of
 * digits with nothing to say what was counted. The picture is `aria-hidden`, so the `<table>` next
 * to it is the single accessible representation rather than a second announcement of the same
 * figures — the split billing.md §5.3/§6 draws for the credit bar, paid here once for the product.
 *
 * Nothing in here is focusable. These are figures, not controls, so rule 10's 3:1 boundary floor
 * and rule 12's 40px target have nothing to bind to, and the 1px `border` hairlines are separators
 * — the one job that token is allowed.
 *
 * Rule 9 is narrower than "no links here": it bans a **zero** inside an `<a>`. The blanket ban is
 * billing.md §6.2's rule for its own table. DASHBOARD_BRIEF's `/app/settings/agents` counters do
 * want linked rows, and this component has no slot for one — adding it is a separate change that
 * takes on rule 10 and rule 12 for the row itself.
 */
@Composable
fun LStatRow(
    rows: List<LStatRowItem>,
    caption: String,
    modifier: Modifier = Modifier,
) {
    if (rows.isEmpty()) return

    Div(
        LStatRowGroupStyle.toModifier()
            .then(modifier)
            .attr("aria-hidden", "true")
            .toAttrs(),
    ) {
        rows.forEachIndexed { index, row ->
            Div(
                LStatRowStyle.toModifier()
                    .then(
                        // Only where something sits above it: a rule at the top of the stack is a
                        // line drawn under nothing.
                        if (row.emphasis && index > 0) {
                            Modifier.styleModifier {
                                property("border-top", "1px solid ${ColorTokens.BorderStrong.value()}")
                            }
                        } else {
                            Modifier
                        },
                    )
                    .toAttrs(),
            ) {
                Span(
                    LStatRowLabelStyle.toModifier()
                        .then(
                            if (row.emphasis) {
                                Modifier.color(ColorTokens.TextPrimary.value()).fontWeight(600)
                            } else {
                                Modifier
                            },
                        )
                        .toAttrs(),
                ) {
                    Text(row.label)
                }
                Span(
                    LStatRowValueStyle.toModifier()
                        .color(
                            // Hard rule 9. A zero is the absence of a figure and reads as one.
                            if (row.isZero) {
                                ColorTokens.TextSecondary.value()
                            } else {
                                ColorTokens.TextPrimary.value()
                            },
                        )
                        // `value/16.5-strong` is 700. The label's `body/15-strong` is 600 — the
                        // two -strong steps are different weights, and this is the heavier one.
                        .then(if (row.emphasis) Modifier.fontWeight(700) else Modifier)
                        .toAttrs(),
                ) {
                    Text(row.value)
                }
            }
        }
    }

    Table(LVisuallyHiddenStyle.toModifier().toAttrs()) {
        Caption { Text(caption) }
        Tbody {
            rows.forEach { row ->
                Tr {
                    Th(Modifier.toAttrs { attr("scope", "row") }) { Text(row.label) }
                    Td { Text(row.value) }
                }
            }
        }
    }
}
